import Debug from '../utils/Debug.js';
import ProfanityFilter from '../utils/ProfanityFilter.js';

/**
 * ChatService - OpenRouter AI chat API client.
 *
 * Manages API key resolution (server proxy → env → localStorage → runtime prompt),
 * per-NPC conversation history (session-only), and completion requests.
 * Includes retry with backoff for rate-limited (429) responses.
 *
 * On Vercel, requests go through /api/chat (API key stays server-side).
 * In local dev, uses client-side API key from .env.local or localStorage.
 */
export default class ChatService {
  static PROXY_URL = '/api/chat';
  static DIRECT_URL = 'https://openrouter.ai/api/v1/chat/completions';
  static DEFAULT_MODEL = 'meta-llama/llama-3.3-70b-instruct';
  static FALLBACK_MODELS = [
    'google/gemma-3-27b-it',
    'qwen/qwen3-235b-a22b'
  ];
  static MAX_TOKENS = 150;
  static STORAGE_KEY = 'trixel_openrouter_api_key';
  static TIMEOUT_MS = 20000;
  static MAX_RETRIES = 1;
  static RETRY_BASE_DELAY_MS = 5000;

  // Client-side rate limiting
  static MAX_MESSAGES_PER_SESSION = 50;
  static MIN_MESSAGE_INTERVAL_MS = 3000; // 3s between messages

  constructor() {
    /** @type {string|null} */
    this._apiKey = null;

    /** @type {Map<string, Array<{role: string, content: string}>>} */
    this._histories = new Map();

    /** @type {number} Total messages sent this session (across all NPCs) */
    this._sessionMessageCount = 0;

    /** @type {number} Timestamp of last sent message */
    this._lastMessageTime = 0;

    /** @type {boolean|null} null = not yet probed, true/false = probe result */
    this._proxyAvailable = null;
  }

  // ====== API Key Management ======

  getApiKey() {
    if (this._apiKey) return this._apiKey;

    // Check Vite env variable (from .env.local)
    const envKey = import.meta.env.VITE_OPENROUTER_API_KEY;
    if (envKey) {
      this._apiKey = envKey;
      return envKey;
    }

    // Check localStorage
    const stored = localStorage.getItem(ChatService.STORAGE_KEY);
    if (stored) {
      this._apiKey = stored;
      return stored;
    }

    return null;
  }

  setApiKey(key) {
    this._apiKey = key;
    localStorage.setItem(ChatService.STORAGE_KEY, key);
  }

  clearApiKey() {
    this._apiKey = null;
    localStorage.removeItem(ChatService.STORAGE_KEY);
  }

  /**
   * Returns true if the service can send messages —
   * either via server proxy or a client-side API key.
   */
  hasApiKey() {
    if (this._proxyAvailable === true) return true;
    return !!this.getApiKey();
  }

  // ====== Conversation History ======

  /**
   * Get or create conversation history for an NPC.
   * Creates with system prompt on first access.
   * @param {string} npcName
   * @param {string} systemPrompt
   * @returns {Array<{role: string, content: string}>}
   */
  getHistory(npcName, systemPrompt) {
    if (!this._histories.has(npcName)) {
      this._histories.set(npcName, [
        { role: 'system', content: systemPrompt }
      ]);
    }
    return this._histories.get(npcName);
  }

  // ====== API Call ======

  /**
   * Send a message and get a response from the AI.
   * Tries server proxy first, falls back to direct API with client key.
   * Retries with backoff on 429 rate limit, tries fallback models.
   * @param {string} npcName - For history tracking
   * @param {string} userMessage - The user's message
   * @param {string} systemPrompt - System prompt for this NPC
   * @returns {Promise<string>} The assistant's reply text
   * @throws {Error} With user-friendly message on failure
   */
  async sendMessage(npcName, userMessage, systemPrompt) {
    // Client-side rate limiting
    if (this._sessionMessageCount >= ChatService.MAX_MESSAGES_PER_SESSION) {
      throw new Error('SESSION_LIMIT');
    }
    const now = Date.now();
    const elapsed = now - this._lastMessageTime;
    if (elapsed < ChatService.MIN_MESSAGE_INTERVAL_MS) {
      throw new Error('TOO_FAST');
    }

    // Determine whether to use proxy or direct
    const useProxy = await this._isProxyAvailable();
    if (!useProxy) {
      const apiKey = this.getApiKey();
      if (!apiKey) throw new Error('No API key configured.');
    }

    this._lastMessageTime = now;
    this._sessionMessageCount++;

    const history = this.getHistory(npcName, systemPrompt);
    history.push({ role: 'user', content: ProfanityFilter.filterAlways(userMessage) });

    // Try primary model, then fallbacks on 429
    const modelsToTry = [ChatService.DEFAULT_MODEL, ...ChatService.FALLBACK_MODELS];

    for (let modelIdx = 0; modelIdx < modelsToTry.length; modelIdx++) {
      const model = modelsToTry[modelIdx];

      for (let attempt = 0; attempt <= ChatService.MAX_RETRIES; attempt++) {
        // Wait before retry (not on first attempt)
        if (attempt > 0) {
          const delay = ChatService.RETRY_BASE_DELAY_MS * attempt;
          await new Promise(r => setTimeout(r, delay));
        }

        try {
          const result = useProxy
            ? await this._doProxyRequest(model, history)
            : await this._doDirectRequest(this.getApiKey(), model, history);
          history.push({ role: 'assistant', content: result });
          return result;
        } catch (err) {
          const is429 = err.message === '429';

          // On 429, retry with same model or try next model
          if (is429) {
            if (attempt < ChatService.MAX_RETRIES) continue; // retry same model
            if (modelIdx < modelsToTry.length - 1) break;    // try next model
            // All models exhausted
            history.pop();
            throw new Error('Rate limited on all models. Wait a moment and try again.');
          }

          // Non-429 errors: don't retry, propagate immediately
          history.pop();
          throw err;
        }
      }
    }

    // Should not reach here, but safety net
    history.pop();
    throw new Error('Could not get a response. Try again later.');
  }

  // ====== Proxy Detection ======

  /**
   * Probe once whether the /api/chat proxy is available.
   * Caches the result for the session.
   */
  async _isProxyAvailable() {
    if (this._proxyAvailable !== null) return this._proxyAvailable;

    try {
      // GET returns { ok: true } (200, application/json) if the proxy exists.
      // Vite SPA fallback returns 200 with text/html → proxy not available.
      const res = await fetch(ChatService.PROXY_URL, { method: 'GET' });
      const ct = res.headers.get('content-type') || '';
      this._proxyAvailable = res.ok && ct.includes('application/json');
    } catch (_) {
      this._proxyAvailable = false;
    }

    return this._proxyAvailable;
  }

  // ====== Request Methods ======

  /**
   * Send request through the Vercel serverless proxy (no API key needed client-side).
   */
  async _doProxyRequest(model, messages) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ChatService.TIMEOUT_MS);

    try {
      const response = await fetch(ChatService.PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model,
          messages,
          max_tokens: ChatService.MAX_TOKENS
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return this._handleResponse(response, model);
    } catch (err) {
      clearTimeout(timeoutId);
      return this._handleFetchError(err);
    }
  }

  /**
   * Send request directly to OpenRouter (client-side API key).
   */
  async _doDirectRequest(apiKey, model, messages) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ChatService.TIMEOUT_MS);

    try {
      const response = await fetch(ChatService.DIRECT_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': 'https://3merillon.com',
          'X-Title': 'Trixel'
        },
        body: JSON.stringify({
          model,
          messages,
          max_tokens: ChatService.MAX_TOKENS
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return this._handleResponse(response, model);
    } catch (err) {
      clearTimeout(timeoutId);
      return this._handleFetchError(err);
    }
  }

  /**
   * Parse response from OpenRouter (same format whether proxied or direct).
   */
  _handleResponse(response, model) {
    if (!response.ok) {
      if (response.status === 401) {
        this.clearApiKey();
        throw new Error('Invalid API key. You\'ll be prompted for a new one next time.');
      }
      if (response.status === 429) {
        response.json().then(body => {
          Debug.warn('ChatService', `429 on ${model}:`, body);
        }).catch(() => {});
        throw new Error('429');
      }
      response.json().then(body => {
        Debug.warn('ChatService', `${response.status} on ${model}:`, body);
      }).catch(() => {});
      throw new Error(`API error (${response.status}). Try again.`);
    }

    return response.json().then(data => {
      const reply = data.choices?.[0]?.message?.content;
      if (!reply) {
        throw new Error('No response received. Try again.');
      }
      return reply;
    });
  }

  /**
   * Handle fetch-level errors (network, abort).
   */
  _handleFetchError(err) {
    if (err.name === 'AbortError') {
      throw new Error('Response timed out. Try again.');
    }

    // Re-throw our own errors
    if (err.message === '429' ||
        err.message.includes('API') || err.message.includes('key') ||
        err.message.includes('response') || err.message.includes('timed out')) {
      throw err;
    }

    throw new Error('Could not reach AI service. Check your connection.');
  }
}
